if (!customElements.get('quick-view-trigger')) {
  class QuickViewTrigger extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);
    }

    _onClick(event) {
      const button = event.target.closest('button, a');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();

      const handle = this.dataset.productHandle;
      const url = this.dataset.productUrl;
      if (!handle) return;

      const drawer = document.querySelector('quick-view-drawer');
      if (!drawer) {
        window.location.href = url || `/products/${handle}`;
        return;
      }
      const hasAnodization = this.dataset.hasAnodization === 'true';
      drawer.open(handle, url, button, hasAnodization);
    }
  }
  customElements.define('quick-view-trigger', QuickViewTrigger);
}

if (!customElements.get('quick-view-drawer')) {
  class QuickViewDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('[data-quick-view-overlay]');
      this.closeBtn = this.querySelector('[data-quick-view-close]');
      this.body = this.querySelector('[data-quick-view-body]');
      this.template = this.querySelector('[data-quick-view-template]');

      this.product = null;
      this.selectedVariant = null;
      this.currentOptions = [];
      this.loading = false;
      this.opener = null;
      this.activeElement = null;
      this.fullUrl = null;
      this.hasAnodization = false;

      this._onEsc = this._onEsc.bind(this);
      this._onOptionChange = this._onOptionChange.bind(this);
      this._onAdd = this._onAdd.bind(this);
      this._onClose = this.close.bind(this);

      if (this.overlay) this.overlay.addEventListener('click', this._onClose);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this._onClose);
    }

    async open(handle, url, opener, hasAnodization) {
      if (this.loading) return;
      this.loading = true;
      this.opener = opener || null;
      this.activeElement = document.activeElement;
      this.hasAnodization = hasAnodization || false;

      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open', 'is-loading');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this._onEsc);

      try {
        if (!handle) throw new Error('handle vazio');
        this.product = await this._fetchProductWithRetry(handle, 2);
        this.fullUrl = url || this.product.url || `/products/${handle}`;
        this._render();
      } catch (err) {
        // Erros de rede/CDN intermitentes — mensagem amigável + ação clara.
        this._renderError('Não foi possível carregar. Tente novamente.');
      } finally {
        this.classList.remove('is-loading');
        this.loading = false;
      }
    }

    async _fetchProductWithRetry(handle, maxRetries) {
      // Retry com backoff linear (250ms, 500ms). Cobre blips de rede / CDN miss
      // que causam falhas intermitentes em /products/{handle}.js.
      let lastErr;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
        }
        try {
          const res = await fetch(`/products/${handle}.js`, {
            headers: { 'Accept': 'application/json' },
            credentials: 'same-origin',
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return await res.json();
        } catch (err) {
          lastErr = err;
        }
      }
      throw lastErr || new Error('quick-view: load failed');
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this._onEsc);
      if (this.body) this.body.innerHTML = '';
      if (this.activeElement && typeof this.activeElement.focus === 'function') {
        try { this.activeElement.focus(); } catch (e) {}
      }
    }

    _onEsc(e) {
      if (e.key === 'Escape' || e.keyCode === 27) this.close();
    }

    _render() {
      if (!this.product || !this.template || !this.body) return;
      this.body.innerHTML = '';
      const frag = this.template.content.cloneNode(true);
      this.body.appendChild(frag);

      this.selectedVariant =
        this.product.variants.find((v) => v.available) || this.product.variants[0];
      this.currentOptions = (this.selectedVariant.options || []).map((option) => this._optionText(option));

      this._renderImage();
      this._renderHeader();
      this._renderDescription();
      this._renderOptions();
      this._renderAnodization();
      this._renderPrice();
      this._renderCta();
      this._renderFullLink();

      requestAnimationFrame(() => {
        const focusTarget =
          this.body.querySelector('[data-quick-view-focus]') ||
          this.body.querySelector('[data-quick-view-add]') ||
          this.closeBtn;
        if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
      });
    }

    _renderError(msg) {
      if (!this.body) return;
      this.body.innerHTML = `<div class="quick-view__error" role="alert">${this._escape(msg)}</div>`;
    }

    _renderImage() {
      const imgEl = this.body.querySelector('[data-quick-view-image]');
      if (!imgEl) return;
      const v = this.selectedVariant;
      const src =
        (v && v.featured_image && v.featured_image.src) || this.product.featured_image;
      if (!src) {
        imgEl.removeAttribute('src');
        imgEl.style.visibility = 'hidden';
        return;
      }
      imgEl.style.visibility = '';
      const sized = this._sizeImage(src, 900);
      imgEl.src = sized;
      imgEl.alt = this.product.title || '';
    }

    _sizeImage(src, width) {
      let absolute = src;
      if (absolute.startsWith('//')) absolute = `https:${absolute}`;
      try {
        const u = new URL(absolute, window.location.origin);
        u.searchParams.set('width', String(width));
        return u.toString();
      } catch (e) {
        return absolute;
      }
    }

    _renderHeader() {
      const titleEl = this.body.querySelector('[data-quick-view-title]');
      const vendorEl = this.body.querySelector('[data-quick-view-vendor]');
      if (titleEl) titleEl.textContent = this._toString(this.product.title);
      if (vendorEl) {
        if (this.product.vendor) {
          vendorEl.textContent = this._toString(this.product.vendor);
          vendorEl.hidden = false;
        } else {
          vendorEl.hidden = true;
        }
      }
    }

    _renderDescription() {
      const descEl = this.body.querySelector('[data-quick-view-description]');
      if (!descEl) return;
      const rawDesc = this._toString(this.product.description);
      const plain = rawDesc.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      if (!plain) {
        descEl.hidden = true;
        return;
      }
      descEl.hidden = false;
      descEl.textContent = plain.length > 220 ? `${plain.slice(0, 217)}…` : plain;
    }

    _renderOptions() {
      const container = this.body.querySelector('[data-quick-view-options]');
      if (!container) return;
      container.innerHTML = '';
      if (this.product.has_only_default_variant || this.product.variants.length <= 1) {
        container.hidden = true;
        return;
      }
      container.hidden = false;

      this.product.options.forEach((optionName, index) => {
        const optionLabel = this._optionText(optionName) || `Opção ${index + 1}`;
        const group = document.createElement('fieldset');
        group.className = 'quick-view__option';

        const legend = document.createElement('legend');
        legend.className = 'quick-view__option-label';
        legend.textContent = optionLabel;
        group.appendChild(legend);

        const pills = document.createElement('div');
        pills.className = 'quick-view__option-pills';
        group.appendChild(pills);

        const rawValues = Array.isArray(optionName?.values)
          ? optionName.values
          : this.product.variants.map((v) => v.options[index]);
        const values = [
          ...new Set(rawValues.map((value) => this._optionText(value)).filter(Boolean)),
        ];
        values.forEach((value) => {
          const idSafe = String(value).replace(/[^a-z0-9]+/gi, '-').toLowerCase();
          const inputId = `qv-opt-${index}-${idSafe}`;

          const label = document.createElement('label');
          label.className = 'quick-view__option-pill';
          label.setAttribute('for', inputId);

          const input = document.createElement('input');
          input.type = 'radio';
          input.id = inputId;
          input.name = `qv-option-${index}`;
          input.value = value;
          input.dataset.optionIndex = String(index);
          input.dataset.optionValue = value;
          input.className = 'quick-view__option-input';
          if (this.selectedVariant && this._optionText(this.selectedVariant.options[index]) === value) {
            input.checked = true;
          }
          input.addEventListener('change', this._onOptionChange);

          const span = document.createElement('span');
          span.textContent = value;

          label.appendChild(input);
          label.appendChild(span);
          pills.appendChild(label);
        });

        container.appendChild(group);
      });

      this._syncOptionAvailability();
    }

    _renderAnodization() {
      const container = this.body.querySelector('[data-quick-view-anodization]');
      if (!container) return;
      container.innerHTML = '';
      if (!this.hasAnodization) {
        container.hidden = true;
        return;
      }

      const colors = window.__vhAnodizationColors || [];
      if (!colors.length) {
        container.hidden = true;
        return;
      }

      container.hidden = false;
      const contextId = `qv-${this.product.id || Date.now()}`;

      const picker = document.createElement('anodization-picker');
      picker.className = 'anodization-picker';
      picker.dataset.contextId = contextId;

      // Header
      const header = document.createElement('div');
      header.className = 'anodization-picker__header';
      header.innerHTML = `
        <div class="anodization-picker__title">
          <span class="anodization-picker__title-text">Cor da anodização do metal</span>
        </div>
        <p class="anodization-picker__hint">Opcional. Sem seleção, enviamos em titânio padrão.</p>
        <div class="anodization-picker__selected" aria-live="polite">
          <span class="anodization-picker__selected-prefix">Selecionada:</span>
          <span class="anodization-picker__selected-value" data-anodization-selected-label>
            <span class="anodization-picker__placeholder">Nenhuma · titânio padrão</span>
          </span>
        </div>
      `;
      picker.appendChild(header);

      // Grid
      const grid = document.createElement('div');
      grid.className = 'anodization-picker__grid';
      grid.setAttribute('role', 'radiogroup');
      grid.setAttribute('aria-label', 'Cor da anodização do metal');

      colors.forEach((color) => {
        const status = (color.status || color['status'] || '').toLowerCase();
        const isDiscontinued = status === 'discontinued';
        const colorHandle = color.handle || color['handle'] || String(Math.random()).slice(2,8);
        const colorName = color.name || color['name'] || colorHandle;
        const colorCode = color.code || color['code'] || '';
        let colorHex = color.hex || color['hex'] || '';
        // Shopify color fields may return an object {red, green, blue, alpha}
        if (typeof colorHex === 'object' && colorHex !== null) {
          if (colorHex.red !== undefined) {
            const r = Math.round(colorHex.red);
            const g = Math.round(colorHex.green);
            const b = Math.round(colorHex.blue);
            colorHex = `rgb(${r}, ${g}, ${b})`;
          } else {
            colorHex = '';
          }
        }
        if (!colorHex || typeof colorHex !== 'string') {
          colorHex = '#cccccc';
        }
        const displayLabel = colorCode ? `${colorName} · ${colorCode}` : colorName;
        const inputId = `anodization-${contextId}-${colorHandle}`;

        const cell = document.createElement('div');
        cell.className = 'anodization-picker__cell';

        const label = document.createElement('label');
        label.className = `anodization-swatch${isDiscontinued ? ' anodization-swatch--discontinued' : ''}`;
        label.setAttribute('for', inputId);
        label.title = displayLabel;

        const input = document.createElement('input');
        input.id = inputId;
        input.type = 'radio';
        input.name = 'properties[Cor da anodização do metal]';
        input.value = displayLabel;
        input.className = 'anodization-swatch__input';
        input.setAttribute('aria-label', displayLabel);
        input.dataset.colorHandle = colorHandle;
        input.dataset.colorLabel = displayLabel;
        if (isDiscontinued) input.disabled = true;

        const visual = document.createElement('span');
        visual.className = 'anodization-swatch__visual';
        visual.style.setProperty('--anodization-swatch-color', colorHex);
        visual.setAttribute('aria-hidden', 'true');

        const swatchImg = color.swatchImage || color['swatchImage'] || '';
        if (swatchImg) {
          const img = document.createElement('img');
          img.src = swatchImg;
          img.alt = '';
          img.className = 'anodization-swatch__image';
          img.width = 40;
          img.height = 40;
          img.loading = 'lazy';
          visual.appendChild(img);
        }

        label.appendChild(input);
        label.appendChild(visual);
        cell.appendChild(label);
        grid.appendChild(cell);
      });

      picker.appendChild(grid);

      // Hidden ref input
      const refInput = document.createElement('input');
      refInput.type = 'hidden';
      refInput.name = 'properties[_anodization_color_ref]';
      refInput.value = '';
      refInput.disabled = true;
      refInput.dataset.anodizationRefInput = '';
      picker.appendChild(refInput);

      container.appendChild(picker);
    }

    _onOptionChange(event) {
      const index = parseInt(event.target.dataset.optionIndex, 10);
      this.currentOptions[index] = event.target.value;
      const newVariant = this.product.variants.find((v) =>
        this.currentOptions.every((val, i) => val === undefined || this._optionText(v.options[i]) === val)
      );
      if (newVariant) {
        this.selectedVariant = newVariant;
        this._syncOptionAvailability();
        this._renderImage();
        this._renderPrice();
        this._renderCta();
      }
    }

    _syncOptionAvailability() {
      if (!this.body || !this.product || !Array.isArray(this.product.variants)) return;
      this.body.querySelectorAll('.quick-view__option-input').forEach((input) => {
        const index = parseInt(input.dataset.optionIndex, 10);
        const value = input.dataset.optionValue;
        const label = input.closest('.quick-view__option-pill');
        if (!label || Number.isNaN(index)) return;

        const isAvailable = this._isOptionValueAvailable(index, value);
        label.classList.toggle('is-unavailable', !isAvailable);
        input.classList.toggle('is-unavailable', !isAvailable);
        input.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
        input.setAttribute('aria-label', isAvailable ? value : `${value} - esgotado`);
      });
    }

    _isOptionValueAvailable(index, value) {
      return this.product.variants.some((variant) => {
        if (!variant.available) return false;
        return variant.options.every((optionValue, optionIndex) => {
          const normalized = this._optionText(optionValue);
          if (optionIndex === index) return normalized === value;
          const selectedValue = this.currentOptions[optionIndex];
          return selectedValue === undefined || selectedValue === '' || normalized === selectedValue;
        });
      });
    }

    _renderPrice() {
      const priceEl = this.body.querySelector('[data-quick-view-price]');
      const compareEl = this.body.querySelector('[data-quick-view-compare-price]');
      if (!priceEl) return;
      const v = this.selectedVariant;
      priceEl.textContent = this._formatMoney(v.price);
      if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.textContent = this._formatMoney(v.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
    }

    _renderCta() {
      const ctaEl = this.body.querySelector('[data-quick-view-add]');
      if (!ctaEl) return;
      const labelEl = ctaEl.querySelector('[data-quick-view-add-label]');
      const v = this.selectedVariant;
      const labelAdd = ctaEl.dataset.labelAdd || 'Adicionar ao carrinho';
      const labelSoldOut = ctaEl.dataset.labelSoldOut || 'Esgotado';
      if (v && v.available) {
        ctaEl.disabled = false;
        ctaEl.dataset.variantId = String(v.id);
        if (labelEl) labelEl.textContent = labelAdd;
      } else {
        ctaEl.disabled = true;
        if (labelEl) labelEl.textContent = labelSoldOut;
      }
      ctaEl.removeEventListener('click', this._onAdd);
      ctaEl.addEventListener('click', this._onAdd);
    }

    _renderFullLink() {
      const linkEl = this.body.querySelector('[data-quick-view-full]');
      if (linkEl) linkEl.href = this.fullUrl || '#';
    }

    async _onAdd(event) {
      event.preventDefault();
      const btn = event.currentTarget;
      if (!this.selectedVariant || !this.selectedVariant.available) return;
      if (btn.dataset.busy === '1') return;

      btn.dataset.busy = '1';
      btn.classList.add('is-loading');
      btn.disabled = true;

      const errEl = this.body.querySelector('[data-quick-view-error]');
      if (errEl) errEl.hidden = true;

      try {
        const formData = new FormData();
        formData.append('id', String(this.selectedVariant.id));
        formData.append('quantity', '1');
        formData.append('sections', 'cart-drawer,cart-icon-bubble');
        formData.append('sections_url', window.location.pathname);

        // Anodization properties
        const anodizationContainer = this.body.querySelector('[data-quick-view-anodization]');
        if (anodizationContainer && !anodizationContainer.hidden) {
          const selectedRadio = anodizationContainer.querySelector('input[type="radio"]:checked');
          const refInput = anodizationContainer.querySelector('[data-anodization-ref-input]');
          if (selectedRadio) {
            formData.append('properties[Cor da anodização do metal]', selectedRadio.value);
          }
          if (refInput && refInput.value) {
            formData.append('properties[_anodization_color_ref]', refInput.value);
          }
        }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/javascript',
          },
          body: formData,
          credentials: 'same-origin',
        });
        let parsed;
        try {
          parsed = await res.json();
        } catch (e) {
          if (!res.ok) throw new Error(`Cart add failed: HTTP ${res.status} (non-JSON response)`);
          throw e;
        }

        if (parsed.status) {
          throw new Error(parsed.message || parsed.description || 'Erro');
        }

        this.close();
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(parsed);
        } else {
          const cartNotification = document.querySelector('cart-notification');
          if (cartNotification && typeof cartNotification.renderContents === 'function') {
            cartNotification.renderContents(parsed);
          } else {
            window.location.href = '/cart';
          }
        }
      } catch (err) {
        if (errEl) {
          errEl.textContent = err && err.message ? err.message : 'Não foi possível adicionar.';
          errEl.hidden = false;
        }
      } finally {
        btn.dataset.busy = '0';
        btn.classList.remove('is-loading');
        btn.disabled = !(this.selectedVariant && this.selectedVariant.available);
      }
    }

    _formatMoney(cents) {
      const value = (cents || 0) / 100;
      try {
        const currency =
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'BRL';
        const locale = (window.Shopify && window.Shopify.locale) || 'pt-BR';
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
      } catch (e) {
        return value.toFixed(2);
      }
    }

    _toString(val) {
      if (val == null) return '';
      if (typeof val === 'string') return val;
      if (typeof val === 'object') {
        // Shopify sometimes returns objects for text fields
        if (val.value && typeof val.value === 'string') return val.value;
        return JSON.stringify(val);
      }
      return String(val);
    }

    _optionText(val) {
      if (val == null) return '';
      if (['string', 'number', 'boolean'].includes(typeof val)) return String(val);
      if (typeof val !== 'object') return String(val);

      const candidateKeys = ['name', 'value', 'label', 'title', 'display_name'];
      for (const key of candidateKeys) {
        const candidate = val[key];
        if (candidate == null) continue;
        if (['string', 'number', 'boolean'].includes(typeof candidate)) return String(candidate);
        if (typeof candidate === 'object') {
          const nested = this._optionText(candidate);
          if (nested) return nested;
        }
      }

      return '';
    }

    _escape(str) {
      const div = document.createElement('div');
      div.textContent = String(str || '');
      return div.innerHTML;
    }
  }

  customElements.define('quick-view-drawer', QuickViewDrawer);
}
